import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import redirects from 'developer-docs-source/redirects.json';
import { getProtectedDocs, getProtectedDocPaths } from 'developer-docs-source';
import { isGuideDoc, isCMSDevelopmentDoc, isGettingStartedDoc, ctxKeyToPath, isCRMCustomizationDoc, isUIComponentsDoc, isAppsDoc, isCmsReferenceDoc, isSupportedApiMdxDoc, GETTING_STARTED_PREFIX, GUIDES_PREFIX, REFERENCE_PREFIX } from 'developer-docs-mdx-components/utils/urls';
import LandingPage from '../containers/LandingPage';
import SearchResultsPage from '../containers/SearchResultsPage';
import keyMirror from 'react-utils/keyMirror';
import MDXPage from '../containers/MDXPage';
import { docsCtxKeyToPath, retrieveAllMdxPaths } from '../utils/mdx';
import NotFound from './NotFound';
import APIReference from './apiReference/APIReferenceLayout';
import { allSpecApiReferenceKeys } from '../config/nav/navUtils';
import { apiGuidesNavConfig } from '../config/nav/guides/apiGuides';
import { cmsDevelopmentNavConfig } from '../config/nav/guides/cmsDevelopment';
import { apiEndpointsNavConfig } from '../config/nav/reference/apiEndpoints';
import { MainLandingPage } from '../containers/MainLandingPage';
import { getStartedNavConfig } from '../config/nav/getStarted/getStarted';
import { crmCustomizationNavConfig } from '../config/nav/guides/crmCustomization';
import { uiComponentsNavConfig } from '../config/nav/reference/uiComponents';
import { appsNavConfig } from '../config/nav/guides/apps';
import { cmsReferenceNavConfig } from '../config/nav/reference/cmsReference';
import { LocaleContextProvider } from 'developer-docs-shared-components/context/LocaleContext';
import { urlKeys } from 'developer-docs-source/urlKeys';
import { useSelector } from 'react-redux';
import { selectUserGates } from '../selectors/userState';
import merge from 'hs-lodash/merge';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const Paths = {
  HOME: '/',
  SEARCH: '/search',
  REFERENCE: `/${REFERENCE_PREFIX}`,
  GUIDES: `/${GUIDES_PREFIX}`,
  GETTING_STARTED: `/${GETTING_STARTED_PREFIX}`,
  REFERENCE_UI_COMPONENTS: '/reference/ui-components'
};

/**
 * Primary nav page types (the main links shown in the header).
 */
export const PageTypes = keyMirror({
  REFERENCE: null,
  GUIDES: null,
  GETTING_STARTED: null
});
const Routes = () => {
  const userGates = useSelector(selectUserGates);
  const gateProtectedInfo = getProtectedDocs(userGates.gates);
  const protectedPaths = getProtectedDocPaths();
  let whitelistedUrlKeys = urlKeys;
  if (gateProtectedInfo) {
    whitelistedUrlKeys = merge(urlKeys, gateProtectedInfo);
  }
  const locale = window.I18N_MANUAL_LANG || 'en-us';
  const docKeys = [...retrieveAllMdxPaths(whitelistedUrlKeys[locale])];
  const {
    commonPath: apiEndpointsIndexUrl
  } = apiEndpointsNavConfig();
  const location = useLocation();

  // track page view
  useEffect(() => {
    const _hsq = window && '_hsq' in window ? window._hsq : [];
    _hsq.push(['setPath', location.pathname]);
    _hsq.push(['trackPageView']);
  }, [location]);

  // Don't display routes until we have the user's gate info
  if (userGates.status.pending) {
    return null;
  }
  return /*#__PURE__*/_jsx(LocaleContextProvider, {
    value: locale,
    children: /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(Route, {
        path: Paths.HOME,
        exact: true,
        children: /*#__PURE__*/_jsx(MainLandingPage, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: `${Paths.SEARCH}*`,
        exact: true,
        children: /*#__PURE__*/_jsx(SearchResultsPage, {})
      }), /*#__PURE__*/_jsx(Redirect, {
        from: Paths.GETTING_STARTED,
        to: `${Paths.GETTING_STARTED}/overview`,
        exact: true
      }), /*#__PURE__*/_jsx(Route, {
        path: `${apiGuidesNavConfig().commonPath}`,
        exact: true,
        children: /*#__PURE__*/_jsx(LandingPage, {
          pageType: PageTypes.GUIDES,
          secondaryNavConfig: apiGuidesNavConfig()
        })
      }), /*#__PURE__*/_jsx(Redirect, {
        from: Paths.GUIDES,
        to: `${Paths.GUIDES}/api`,
        exact: true
      }), /*#__PURE__*/_jsx(Redirect, {
        from: `${Paths.GUIDES}/cms`,
        to: `${Paths.GUIDES}/cms/overview`,
        exact: true
      }), /*#__PURE__*/_jsx(Redirect, {
        from: `${Paths.GUIDES}/crm`,
        to: `${Paths.GUIDES}/crm/overview`,
        exact: true
      }), /*#__PURE__*/_jsx(Redirect, {
        from: `${Paths.REFERENCE}/cms`,
        to: `${Paths.REFERENCE}/cms/overview`,
        exact: true
      }), /*#__PURE__*/_jsx(Redirect, {
        from: `${Paths.REFERENCE}/ui-components`,
        to: `${Paths.REFERENCE}/ui-components/overview`,
        exact: true
      }), /*#__PURE__*/_jsx(Redirect, {
        from: `${Paths.GUIDES}/apps`,
        to: `${Paths.GUIDES}/apps/overview`,
        exact: true
      }), /*#__PURE__*/_jsx(Redirect, {
        from: `${Paths.REFERENCE_UI_COMPONENTS}/ui-extension-components`,
        to: `${Paths.REFERENCE_UI_COMPONENTS}/overview`,
        exact: true
      }), /*#__PURE__*/_jsx(Redirect, {
        from: `${Paths.GUIDES}/apps/marketplace/app-accelerator-program-terms`,
        to: `${Paths.GUIDES}/apps/overview`,
        exact: true
      }), /*#__PURE__*/_jsx(Redirect, {
        from: `${Paths.GUIDES}/api/webhooks/overview`,
        to: `${Paths.GUIDES}/api/app-management/webhooks`,
        exact: true
      }), /*#__PURE__*/_jsx(Redirect, {
        from: `${Paths.GUIDES}/api/oauth/tokens`,
        to: `${Paths.GUIDES}/api/app-management/oauth-tokens`,
        exact: true
      }), Object.keys(redirects).map(docsCtxKey => {
        return /*#__PURE__*/_jsx(Redirect, {
          from: docsCtxKeyToPath(docsCtxKey),
          to: docsCtxKeyToPath(redirects[docsCtxKey])
        }, docsCtxKey);
      }), docKeys.map(docsCtxKey => {
        const path = ctxKeyToPath(docsCtxKey);
        if (isGuideDoc(docsCtxKey, gateProtectedInfo)) {
          // Guide layout
          return /*#__PURE__*/_jsx(Route, {
            path: path,
            exact: true,
            children: /*#__PURE__*/_jsx(MDXPage, {
              ctxKey: docsCtxKey,
              pageType: PageTypes.GUIDES,
              secondaryNavConfig: apiGuidesNavConfig()
            })
          }, path);
        }
        if (isCMSDevelopmentDoc(docsCtxKey, gateProtectedInfo)) {
          return /*#__PURE__*/_jsx(Route, {
            path: path,
            exact: true,
            children: /*#__PURE__*/_jsx(MDXPage, {
              ctxKey: docsCtxKey,
              pageType: PageTypes.GUIDES,
              secondaryNavConfig: cmsDevelopmentNavConfig()
            })
          }, path);
        }
        if (isCRMCustomizationDoc(docsCtxKey, gateProtectedInfo)) {
          return /*#__PURE__*/_jsx(Route, {
            path: path,
            exact: true,
            children: /*#__PURE__*/_jsx(MDXPage, {
              ctxKey: docsCtxKey,
              pageType: PageTypes.GUIDES,
              secondaryNavConfig: crmCustomizationNavConfig()
            })
          }, path);
        }
        if (isUIComponentsDoc(docsCtxKey, gateProtectedInfo)) {
          return /*#__PURE__*/_jsx(Route, {
            path: path,
            exact: true,
            children: /*#__PURE__*/_jsx(MDXPage, {
              ctxKey: docsCtxKey,
              pageType: PageTypes.REFERENCE,
              secondaryNavConfig: uiComponentsNavConfig()
            })
          }, path);
        }
        if (isAppsDoc(docsCtxKey, gateProtectedInfo)) {
          return /*#__PURE__*/_jsx(Route, {
            path: path,
            exact: true,
            children: /*#__PURE__*/_jsx(MDXPage, {
              ctxKey: docsCtxKey,
              pageType: PageTypes.GUIDES,
              secondaryNavConfig: appsNavConfig()
            })
          }, path);
        }
        if (isCmsReferenceDoc(docsCtxKey, gateProtectedInfo)) {
          // FIXME: Need to move one doc in /docs/cms/
          const computedPath = path.startsWith('/docs/cms/') ? path.replace('/docs/cms/', `${Paths.REFERENCE}/cms/`) : path;
          return /*#__PURE__*/_jsx(Route, {
            path: computedPath,
            exact: true,
            children: /*#__PURE__*/_jsx(MDXPage, {
              ctxKey: docsCtxKey,
              pageType: PageTypes.GUIDES,
              secondaryNavConfig: cmsReferenceNavConfig()
            })
          }, computedPath);
        }
        if (isGettingStartedDoc(docsCtxKey, gateProtectedInfo)) {
          return /*#__PURE__*/_jsx(Route, {
            path: path,
            exact: true,
            children: /*#__PURE__*/_jsx(MDXPage, {
              ctxKey: docsCtxKey,
              pageType: PageTypes.GETTING_STARTED,
              secondaryNavConfig: getStartedNavConfig()
            })
          }, path);
        }
        if (isSupportedApiMdxDoc(docsCtxKey, gateProtectedInfo)) {
          return /*#__PURE__*/_jsx(Route, {
            path: path,
            exact: true,
            children: /*#__PURE__*/_jsx(MDXPage, {
              ctxKey: docsCtxKey,
              pageType: PageTypes.REFERENCE,
              secondaryNavConfig: apiEndpointsNavConfig()
            })
          }, path);
        }
        return null;
      }), /*#__PURE__*/_jsx(Redirect, {
        from: Paths.REFERENCE,
        to: `${apiEndpointsIndexUrl}`,
        exact: true
      }), /*#__PURE__*/_jsx(Route, {
        path: apiEndpointsIndexUrl,
        exact: true,
        children: /*#__PURE__*/_jsx(LandingPage, {
          pageType: PageTypes.REFERENCE,
          secondaryNavConfig: apiEndpointsNavConfig()
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: cmsReferenceNavConfig().commonPath,
        exact: true,
        children: /*#__PURE__*/_jsx(LandingPage, {
          pageType: PageTypes.REFERENCE,
          secondaryNavConfig: cmsReferenceNavConfig()
        })
      }), allSpecApiReferenceKeys.map(apiKey => {
        return /*#__PURE__*/_jsx(Route, {
          path: `${apiKey}/:versionId?`,
          exact: true,
          children: /*#__PURE__*/_jsx(APIReference, {
            apiKey: apiKey
          })
        }, apiKey);
      }), protectedPaths.map(path => {
        return /*#__PURE__*/_jsx(Route, {
          path: path,
          exact: true,
          children: /*#__PURE__*/_jsx(NotFound, {
            protectedDoc: true
          })
        }, path);
      }), /*#__PURE__*/_jsx(Route, {
        path: "*",
        children: /*#__PURE__*/_jsx(NotFound, {})
      })]
    })
  });
};
export default Routes;